/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { FunctionComponent, JSX } from "react";

import type { RecordingOptionRowProps } from "./RecordingOptionRowProps";

import "./RecordingOptionRow.scss";

/**
 * RecordingOptionRow — one configurable option rendered as a native
 * <button> so keyboard activation and screen-reader semantics come for free.
 * `aria-haspopup` signals that activating the row opens a value picker.
 * Highlight is driven purely by the `isFocused` prop.
 */
const RecordingOptionRow: FunctionComponent<RecordingOptionRowProps> = (
  props: RecordingOptionRowProps
): JSX.Element => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, sonarjs/no-unused-vars, sonarjs/no-dead-store -- Required by CodingGuidelines §7 (D-09)
  const TAG: string = "RecordingOptionRow";
  const { optionListType, label, description, value, isFocused, onSelect } =
    props;

  const className: string =
    "series-recording-options__row" +
    (isFocused ? " series-recording-options__row--focused" : "");

  return (
    <button
      type="button"
      className={className}
      tabIndex={0}
      aria-haspopup="true"
      aria-label={`${label}, ${value}`}
      onClick={(): void => onSelect(optionListType)}
    >
      <span className="series-recording-options__row-text">
        <span className="series-recording-options__row-label">{label}</span>
        <span className="series-recording-options__row-description">
          {description}
        </span>
      </span>
      <span className="series-recording-options__row-value">{value}</span>
    </button>
  );
};

export default RecordingOptionRow;
